import {
  DEFAULT_SCAN_ID_PATTERNS,
  SIMILARITY_THRESHOLD,
  evaluatePerson,
  looseExtId,
  normalizeExtId,
  normalizeNameString,
  scanVariants,
  similarity,
  type SnapshotRecord,
} from '@jalsa/shared';

import type { Candidate, CheckMode, CheckReason, CheckResult, PersonBrief } from '../types';

export interface LocalOutcome {
  result: CheckResult;
  reason?: CheckReason;
  person?: PersonBrief;
  candidates?: Candidate[];
}

export interface LocalCheckOptions {
  scanPrefixes?: string[];
  /** Muster, mit denen die Jamaat-ID aus einem Kartencode gelöst wird. */
  scanPatterns?: string[];
  candidateLimit?: number;
  now?: () => Date;
}

/**
 * Die Prüfung auf dem Gerät.
 *
 * Sie benutzt dieselben Funktionen wie der Server: Normalisierung,
 * Bewertung und Ähnlichkeit stammen aus @jalsa/shared. Zwei Fassungen
 * würden auseinanderlaufen, und dieselbe Person wäre offline GRÜN und
 * online ROT.
 *
 * Eine Abweichung ist bekannt und hingenommen: der Abzug enthält nur
 * Personen des eigenen Bereichs, deshalb meldet das Gerät bei einer Person
 * mit fremdem Bereich `not_found`, wo der Server `wrong_area` meldet.
 * Betrieblich bedeutet beides dasselbe.
 */
export class LocalCheck {
  private readonly scanPrefixes: string[];
  private readonly scanPatterns: string[];
  private readonly candidateLimit: number;
  private readonly now: () => Date;

  constructor(
    private readonly records: SnapshotRecord[],
    private readonly area: string,
    options: LocalCheckOptions = {},
  ) {
    this.scanPrefixes = options.scanPrefixes ?? ['MKAD'];
    this.scanPatterns = options.scanPatterns ?? [...DEFAULT_SCAN_ID_PATTERNS];
    this.candidateLimit = options.candidateLimit ?? 10;
    this.now = options.now ?? (() => new Date());
  }

  check(mode: CheckMode, query?: string, personId?: string): LocalOutcome {
    if (personId) return this.byPersonId(personId);
    if (!query?.trim()) return { result: 'red', reason: 'not_found' };
    return mode === 'name' ? this.byName(query) : this.byExtId(query);
  }

  private byExtId(query: string): LocalOutcome {
    const norm = normalizeExtId(query);
    if (!norm) return { result: 'red', reason: 'not_found' };

    const variants = scanVariants(norm, this.scanPrefixes, this.scanPatterns);

    for (const variant of variants) {
      const exact = this.records.find((r) => r.extIdNorm === variant);
      if (exact) return this.verdictFor(exact);
    }

    const loose = new Set(variants.map((v) => looseExtId(v)).filter(Boolean) as string[]);
    const matches = this.records
      .filter((r) => r.extIdLoose && loose.has(r.extIdLoose))
      .slice(0, this.candidateLimit);

    if (matches.length === 0) return { result: 'red', reason: 'not_found' };

    // "00123" und "123" können zwei Personen sein. Deshalb GELB, nie GRÜN.
    return {
      result: 'yellow',
      reason: matches.length === 1 ? 'loose_id_match' : 'ambiguous',
      candidates: matches.map((r) => this.candidate(r)),
    };
  }

  private byName(query: string): LocalOutcome {
    const norm = normalizeNameString(query);
    if (!norm) return { result: 'red', reason: 'not_found' };

    const exact = this.records.filter((r) => r.nameNorm === norm);
    if (exact.length === 1) return this.verdictFor(exact[0]);

    const scored = this.records
      .map((record) => ({ record, sim: similarity(record.nameNorm, norm) }))
      .filter((entry) => entry.sim >= SIMILARITY_THRESHOLD)
      .sort((a, b) => b.sim - a.sim || a.record.lastName.localeCompare(b.record.lastName))
      .slice(0, this.candidateLimit);

    if (scored.length === 0) return { result: 'red', reason: 'not_found' };

    return {
      result: 'yellow',
      reason: 'ambiguous',
      candidates: scored.map((entry) => this.candidate(entry.record, entry.sim)),
    };
  }

  private byPersonId(personId: string): LocalOutcome {
    const record = this.records.find((r) => r.id === personId);
    return record ? this.verdictFor(record) : { result: 'red', reason: 'not_found' };
  }

  private verdictFor(record: SnapshotRecord): LocalOutcome {
    const verdict = evaluatePerson(record, this.area, this.now());
    return {
      result: verdict.result,
      ...(verdict.reason ? { reason: verdict.reason as CheckReason } : {}),
      person: {
        id: record.id,
        firstName: record.firstName,
        lastName: record.lastName,
        jamaat: record.jamaat,
        extId: record.extId,
      },
    };
  }

  private candidate(record: SnapshotRecord, sim?: number): Candidate {
    const verdict = evaluatePerson(record, this.area, this.now());
    return {
      personId: record.id,
      firstName: record.firstName,
      lastName: record.lastName,
      jamaat: record.jamaat,
      extIdTail: record.extId.slice(-3),
      ...(sim === undefined ? {} : { similarity: sim }),
      wouldBe: verdict.result,
      ...(verdict.reason ? { reason: verdict.reason as CheckReason } : {}),
    };
  }
}
